'use es6';

import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { ExpandingTextArea } from 'foundations-components/transitional/components/ExpandingTextArea';
import { Fieldset } from 'foundations-components/transitional/components/Fieldset';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { KeyTypes, PopoverType } from '../../constants/popover';
import { applyLazyLoadingToEmbed, extractLoadingValue, sanitizeEmbedHtml, validateEmbedHtml } from '../../utils/embedSrc';
import ExplicitSaveControls from '../ExplicitSaveControls';
import LazyLoadingInput from '../LazyLoadingInput';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = suffix => `RichTextLib.embed.popoverBody.${suffix}`;
const propTypes = {
  embed: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired
};
function EmbedPopoverBodyEdit(props) {
  const {
    embed: initialEmbedCode,
    onSubmit,
    onRemove,
    onClose
  } = props;
  const [embedCode, setEmbedCode] = useState(initialEmbedCode);
  const [hasErrors, setHasErrors] = useState(false);
  const [loadingValue, setLoadingValue] = useState(extractLoadingValue(initialEmbedCode));
  const embedInputRef = useRef(null);
  useEffect(() => {
    setEmbedCode(initialEmbedCode);
  }, [initialEmbedCode]);
  const handleCloseOnEscape = evt => {
    if (evt.key === KeyTypes.ESCAPE) {
      onClose();
    }
  };
  const handleChange = evt => {
    const newEmbedCode = evt.target.value;
    setEmbedCode(newEmbedCode);
    setHasErrors(false);
    setLoadingValue(extractLoadingValue(newEmbedCode));
  };
  const handleLoadingValueChange = evt => {
    const {
      value
    } = evt.target;
    const embedCodeWithLoading = applyLazyLoadingToEmbed(embedCode, value);
    handleChange({
      target: {
        value: embedCodeWithLoading
      }
    });
  };
  const handleSubmit = () => {
    const embed = sanitizeEmbedHtml(embedCode);
    if (validateEmbedHtml(embed)) {
      onRemove();
      onSubmit({
        embed
      });
    } else {
      setHasErrors(true);
      embedInputRef.current.focus();
    }
  };
  const validationMessage = hasErrors ? I18n.text(i18nKey('embedCodeError')) : '';
  return /*#__PURE__*/_jsxs(Fieldset, {
    "data-test-id": "embed-popover-body-edit",
    padding: "500",
    inlineSize: "300px",
    _size: "small",
    tabIndex: "-1",
    onKeyDown: handleCloseOnEscape,
    children: [/*#__PURE__*/_jsx(Text, {
      as: "h4",
      marginBlockEnd: "200",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('title')
      })
    }), /*#__PURE__*/_jsx(FormControl, {
      htmlFor: "embed_txtarea",
      label: I18n.text(i18nKey('embedCodeLabel')),
      tooltip: I18n.text(i18nKey('embedCodeHelp')),
      tooltipPlacement: "top",
      tooltipProps: {
        onMouseDown: evt => evt.preventDefault()
      },
      error: hasErrors,
      validationMessage: validationMessage,
      children: /*#__PURE__*/_jsx(ExpandingTextArea, {
        name: "embed_txtarea",
        "data-gramm": false // Disable Grammarly
        ,
        defaultValue: initialEmbedCode,
        inputRef: embedInputRef,
        minHeight: 180,
        maxHeight: 180,
        shrink: true,
        value: embedCode,
        onChange: handleChange
      })
    }), loadingValue && /*#__PURE__*/_jsx(LazyLoadingInput, {
      onChange: handleLoadingValueChange,
      value: loadingValue,
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "RichTextLib.lazyLoading.embedLabel"
      })
    }), /*#__PURE__*/_jsx(ExplicitSaveControls, {
      paddingBlockStart: "500",
      popoverType: PopoverType.EDIT,
      isSubmitDisabled: embedCode === initialEmbedCode,
      onSubmit: handleSubmit,
      onCancel: onClose
    })]
  });
}
EmbedPopoverBodyEdit.propTypes = propTypes;
export default EmbedPopoverBodyEdit;